import { Controller } from "@hotwired/stimulus"

const PRICING_TIERS = [
  { key: "t01_19", min: 1, max: 19 },
  { key: "t20_29", min: 20, max: 29 },
  { key: "t30_39", min: 30, max: 39 },
  { key: "t40_49", min: 40, max: 49 },
  { key: "t50_plus", min: 50, max: null },
]

export default class extends Controller {
  static targets = [
    "allFunds",
    "share",
    "tierCard",
    "newSelectionBadge",
    "allFundsRow",
    "allFundsBadge",
    "query",
    "fundRow",
    "category"
  ]

  connect() {
    this.initialCheckedByValue = new Map()
    this.shareTargets.forEach((cb) => {
      const key = cb.value || cb.id
      this.initialCheckedByValue.set(key, cb.checked)
    })

    if (this.hasAllFundsTarget && this.allFundsTarget.checked) {
      this.checkAll(true)
    } else {
      this.refreshAllFundsCheckbox()
    }

    this.updatePricingBadges()
  }

  toggleAllFunds() {
    if (!this.hasAllFundsTarget) return

    if (this.allFundsTarget.checked) {
      this.checkAll(true)
    } else {
      this.restoreInitialChecked()
    }

    this.updatePricingBadges()
  }

  toggleShare() {
    this.refreshAllFundsCheckbox()
    this.updatePricingBadges()
  }

  checkAll(checked) {
    this.shareTargets.forEach((cb) => {
      cb.checked = checked
      cb.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  restoreInitialChecked() {
    this.shareTargets.forEach((cb) => {
      const key = cb.value || cb.id
      cb.checked = this.initialCheckedByValue.get(key) || false
      cb.dispatchEvent(new Event("change", { bubbles: true }))
    })
    if (this.hasAllFundsTarget) this.allFundsTarget.checked = false
  }

  refreshAllFundsCheckbox() {
    if (!this.hasAllFundsTarget) return
    if (this.shareTargets.length === 0) return
    this.allFundsTarget.checked = this.shareTargets.every((cb) => cb.checked)
  }

  updatePricingBadges() {
    const currentTierKey = this.getCurrentTierKeyFromDOM()
    const currentAllFunds = this.currentAllFunds()
    const selectedTierKey = this.getSelectedTierKey()
    const allFundsSelected = this.hasAllFundsTarget && this.allFundsTarget.checked

    // RESET CARDS
    this.tierCardTargets.forEach((card) => {
      card.classList.remove(
        "border-orange-500",
        "shadow-md",
        "ring-1",
        "ring-orange-200"
      )
    })

    this.newSelectionBadgeTargets.forEach((el) =>
      el.classList.add("hidden")
    )

    // RESET ALL FUNDS ROW
    if (this.hasAllFundsRowTarget) {
      this.allFundsRowTarget.classList.remove(
        "border-orange-500",
        "shadow-md",
        "ring-1",
        "ring-orange-200"
      )
    }

    if (this.hasAllFundsBadgeTarget) {
      this.allFundsBadgeTarget.classList.add("hidden")
    }

    // CAS 1 : S'abonner à tous les fonds
    if (allFundsSelected) {
      if (currentAllFunds) return

      if (this.hasAllFundsBadgeTarget) {
        this.allFundsBadgeTarget.classList.remove("hidden")
      }

      if (this.hasAllFundsRowTarget) {
        this.allFundsRowTarget.classList.remove("border-gray-200")
        this.allFundsRowTarget.classList.add(
          "border-orange-500",
          "shadow-md",
          "ring-1",
          "ring-orange-200"
        )
      }

      return
    }

    // Franchissement de palier
    const shouldShow =
      selectedTierKey &&
      (
        (currentAllFunds && !allFundsSelected) ||
        (currentTierKey && selectedTierKey !== currentTierKey)
      )

    if (!shouldShow) return

    const card = this.tierCardTargets.find(
      (el) => el.dataset.tierKey === selectedTierKey
    )
    if (!card) return

    const badge = card.querySelector(
      '[data-subscription-all-funds-target="newSelectionBadge"]'
    )
    if (badge) badge.classList.remove("hidden")

    card.classList.remove("border-gray-200")
    card.classList.add(
      "border-orange-500",
      "shadow-md",
      "ring-1",
      "ring-orange-200"
    )
  }

  getSelectedTierKey() {
    const selectedCount = this.shareTargets.filter((cb) => cb.checked).length
    return this.findTierKeyForCount(selectedCount)
  }

  findTierKeyForCount(count) {
    const n = Number(count || 0)
    const tier = PRICING_TIERS.find((t) => {
      if (n < t.min) return false
      if (t.max === null) return true
      return n <= t.max
    })
    return tier ? tier.key : null
  }

  getCurrentTierKeyFromDOM() {
    const currentCard = this.tierCardTargets.find((el) => el.dataset.activeTier === "true")
    return currentCard ? String(currentCard.dataset.tierKey) : null
  }

  currentAllFunds() {
    return this.hasAllFundsRowTarget && this.allFundsRowTarget.dataset.activeAllFunds === "true"
  }

  filter() {
    const q = (this.queryTarget.value || "").trim().toLowerCase()
    const hasQuery = q.length > 0

    // 1) Filtrer les funds
    this.fundRowTargets.forEach((row) => {
      const name = row.dataset.fundName || ""
      const isins = row.dataset.shareIsins || ""
      const company = row.dataset.companyName || ""

      const match =
        q.length === 0 ||
        name.includes(q) ||
        isins.includes(q) ||
        company.includes(q)

      row.classList.toggle("hidden", !match)
    })

    // 2) Catégories: masquer si vide + déplier si query
    this.categoryTargets.forEach((cat) => {
      const rows = cat.querySelectorAll('[data-subscription-all-funds-target="fundRow"]')
      const anyVisible = Array.from(rows).some((r) => !r.classList.contains("hidden"))

      cat.classList.toggle("hidden", !anyVisible)

      if (hasQuery) {
        cat.open = anyVisible
      } else {
        cat.open = false
      }
    })
  }

  uncheckAll() {
    this.shareTargets.forEach((checkbox) => {
      checkbox.checked = false
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  clear() {
    this.queryTarget.value = ""
    this.filter()
    this.queryTarget.focus()
  }

  onKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault() // empêche submit
      return
    }

    if (e.key === "Escape") this.clear()
  }
}
